import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

import styles from '../styles.module.scss'

import { isApiError } from '@/api'
import { adminApplyReferral } from '@/api/internal'
import Button from '@/components/Button'
import FormField from '@/components/FormField/FormField'
import { useToast } from '@/components/Toast/ToastContext'

const referralSchema = z.object({
  user_email: z.string().email('Please enter a valid email'),
  referral_code: z.string().min(1, 'Referral code cannot be empty'),
})

type ReferralFormValues = z.infer<typeof referralSchema>

const ApplyReferralTab = () => {
  const { updateToast } = useToast()

  const {
    control,
    handleSubmit: formHandleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReferralFormValues>({
    resolver: zodResolver(referralSchema),
  })

  const mutation = useMutation({
    mutationFn: (data: ReferralFormValues) =>
      adminApplyReferral({
        user_email: data.user_email,
        referral_code: data.referral_code,
      }),
    onSuccess: (result) => {
      updateToast(result.data.message, 'success')
      reset()
    },
    onError: (err) => {
      console.error(err)
      const message = isApiError(err) ? (err.detail ?? err.message) : 'Failed to apply referral'
      updateToast(message, 'error')
    },
  })

  const handleSubmit = formHandleSubmit((data) => mutation.mutate(data))

  return (
    <form className={styles.formSection} onSubmit={(e) => e.preventDefault()}>
      <div className={styles.formFields}>
        <h2 className={styles.sectionTitle}>Apply Referral Code</h2>
        <p className={styles.infoText}>
          Apply a referral code to a user who signed up without clicking a referral link.
        </p>
        <FormField
          name="user_email"
          label="User Email"
          type="email"
          control={control}
          error={errors.user_email}
          placeholder="user@example.com"
        />
        <FormField
          name="referral_code"
          label="Referral Code"
          control={control}
          error={errors.referral_code}
          placeholder="e.g. JOHN1"
        />
        <Button title="Apply Referral" onClick={handleSubmit} type="dark" isLoading={mutation.isPending} isDisabled={mutation.isPending} />
      </div>
    </form>
  )
}

export default ApplyReferralTab
